'use client';

import React, { ReactNode, useMemo, useState } from 'react';
import {
    Building2,
    Calendar,
    CircleUser,
    RadioTower,
    Search,
    Smile,
    TableCellsSplit,
    Warehouse,
    Wifi,
} from 'lucide-react';
import Select, { InputActionMeta, OptionProps, SingleValue, SingleValueProps, StylesConfig } from 'react-select';
import { useDeviceType } from '@/hooks/useIsMobile';
import { HubMapItem, HubType as HubTypeEnum } from '@/types/hubs';
import IconAdaptive from '@/components/IconAdaptive';

export interface OptionType {
    value: string | null;
    name: string | null;
    subName: string | null;
    hubType: string;
}

const convertHubToOptions = (hubs: HubMapItem[]): OptionType[] => {
    return hubs.map((hub: HubMapItem) => {
        return {
            value: hub.hub_index,
            name: hub.hub_name,
            subName: hub.hub_sub_name,
            hubType: hub.hub_type,
        };
    });
};

const getHubIcon = (hub_type?: string): ReactNode => {
    switch (hub_type) {
        case HubTypeEnum.spot:
            return (
                <IconAdaptive
                    Icon={RadioTower}
                    baseSize={20}
                    breakpoints={{ md: 20 }}
                    className="text-color-hub-spot-pin"
                />
            );
        case HubTypeEnum.festa:
            return (
                <IconAdaptive
                    Icon={Calendar}
                    baseSize={20}
                    breakpoints={{ md: 20 }}
                    className="text-color-hub-festa-pin"
                />
            );
        case HubTypeEnum.stand:
            return (
                <IconAdaptive
                    Icon={TableCellsSplit}
                    baseSize={20}
                    breakpoints={{ md: 20 }}
                    className="text-color-hub-stand-pin"
                />
            );
        case HubTypeEnum.studio:
            return (
                <IconAdaptive
                    Icon={Warehouse}
                    baseSize={20}
                    breakpoints={{ md: 20 }}
                    className="text-color-hub-studio-pin"
                />
            );
        case HubTypeEnum.schoomyers:
            return (
                <IconAdaptive
                    Icon={Smile}
                    baseSize={20}
                    breakpoints={{ md: 20 }}
                    className="text-color-hub-schoomyers-pin"
                />
            );
        case HubTypeEnum.crew:
            return (
                <IconAdaptive
                    Icon={CircleUser}
                    baseSize={20}
                    breakpoints={{ md: 20 }}
                    className="text-color-hub-crew-pin"
                />
            );
        case HubTypeEnum.stage:
            return (
                <IconAdaptive
                    Icon={Building2}
                    baseSize={20}
                    breakpoints={{ md: 20 }}
                    className="text-color-hub-stage-pin"
                />
            );
        case HubTypeEnum.in:
            return (
                <IconAdaptive Icon={Wifi} baseSize={20} breakpoints={{ md: 20 }} className="text-color-hub-in-pin" />
            );
        default:
            return null;
    }
};

export function truncateLabel(text: string, max: number): string {
    if (text.length <= max) return text;
    return text.slice(0, max) + '...';
}

const CustomSingleValue: React.FC<SingleValueProps<OptionType>> = ({ data }) => {
    const { isMobile } = useDeviceType();
    const maxName = isMobile ? 13 : 26;
    const maxSubName = isMobile ? 5 : 30;
    return (
        <div className="flex items-center">
            <div className="mr-2">{getHubIcon(data.hubType)}</div>
            <div className="flex flex-row md:flex-col w-full">
                <div className="flex font-corp text-sm md:text-base text-color-base">
                    {truncateLabel(data.name ?? '', maxName)}
                </div>
                <div className="flex items-center font-notojp font-medium text-xs text-color-co-gray-dark pl-2 md:pl-0 md:-mt-0.5">
                    {truncateLabel(data.subName ?? '', maxSubName)}
                </div>
            </div>
        </div>
    );
};

const CustomOption: React.FC<OptionProps<OptionType>> = (props) => {
    return (
        <div {...props.innerProps} className="flex items-center p-2">
            <div className="mr-2">{getHubIcon(props.data.hubType)}</div>
            <div className="flex flex-col w-full">
                <div className="flex font-corp text-sm md:text-base text-color-base">
                    {truncateLabel(props.data.name ?? '', 50)}
                </div>
                <div className="flex font-notojp font-medium text-xs text-color-co-gray-dark -mt-0.5">
                    {truncateLabel(props.data.subName ?? '', 50)}
                </div>
            </div>
        </div>
    );
};

const CustomStyles = (isMobile: boolean): StylesConfig<OptionType, false> => ({
    menu: (provided) => ({
        ...provided,
        zIndex: 1000,
        marginTop: '12px',
        width: isMobile ? '310px' : '510px',
        marginLeft: '-10px',
    }),
    menuList: (provided) => ({
        ...provided,
        cursor: 'pointer',
    }),
    menuPortal: (provided) => ({
        ...provided,
        zIndex: 10000,
        backgroundColor: '#FFF',
    }),
    placeholder: (provided) => ({
        ...provided,
        fontSize: provided.fontSize,
        color: '#B6B6B6',
        textAlign: 'left',
        lineHeight: '26px',
    }),
    valueContainer: (provided) => ({
        ...provided,
        display: 'flex',
        alignItems: 'center',
        padding: '0 10px',
        justifyContent: 'flex-start',
    }),
    control: (provided, state) => ({
        ...provided,
        minHeight: isMobile ? '30px' : '42px',
        height: isMobile ? '30px' : '42px',
        borderRadius: '0rem',
        backgroundColor: state.isFocused ? '#FFFFFF' : '#F1F1F1',
        borderColor: state.isFocused ? '#8E8E8E' : 'transparent',
        boxShadow: state.isFocused ? '#8E8E8E' : provided.boxShadow,
        cursor: 'pointer',
        '&:hover': {
            borderColor: state.isFocused ? '#8E8E8E' : 'transparent',
        },
    }),
    input: (provided) => ({
        ...provided,
        margin: 0,
        padding: 0,
        lineHeight: isMobile ? '26px' : '42px',
    }),
    option: (provided) => ({
        ...provided,
        cursor: 'pointer',
    }),
});

interface SearchPlaceholderProps {
    placeholder: string;
}

const SearchPlaceholder = ({ placeholder }: SearchPlaceholderProps) => (
    <div className="text-gray-400">{placeholder}</div>
);

export interface HubMapSearchProps {
    hubMaps: HubMapItem[];
    setSelectedHubIndex: (hubIndex: string) => void;
}

export default function HubMapSearch({ hubMaps, setSelectedHubIndex }: HubMapSearchProps) {
    const [selectedOption, setSelectedOption] = useState<OptionType | null>(null);
    const options = useMemo(() => convertHubToOptions(hubMaps), [hubMaps]);
    const [inputValue, setInputValue] = useState('');
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const { isMobile } = useDeviceType();
    return (
        <form
            className="bg-color-white-base/50 backdrop-blur-md shadow-md rounded px-3 md:py-2 flex items-center gap-2"
            onSubmit={(e) => {
                e.preventDefault();
            }}
        >
            <Select
                instanceId="hub-map-select"
                inputId="hub-map-select"
                options={options}
                components={{
                    SingleValue: CustomSingleValue,
                    Option: CustomOption,
                    DropdownIndicator: () => null,
                    IndicatorSeparator: () => null,
                    ClearIndicator: () => null,
                }}
                styles={CustomStyles(isMobile)}
                className="font-notojp font-medium text-base text-color-base w-[260px] md:w-[460px] py-2 md:py-0 -mt-1"
                menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                menuPosition="fixed"
                placeholder={<SearchPlaceholder placeholder={`ピンを検索`} />}
                isClearable
                filterOption={(option, rawInput) => {
                    const keyword = rawInput.trim().toLowerCase();
                    if (!keyword) return true;
                    const { name, subName } = option.data;
                    return `${name ?? ''}${subName ?? ''}`.toLowerCase().includes(keyword);
                }}
                inputValue={inputValue}
                value={selectedOption}
                menuIsOpen={isMenuOpen}
                openMenuOnFocus={true}
                onMenuOpen={() => setIsMenuOpen(true)}
                onMenuClose={() => setIsMenuOpen(false)}
                onInputChange={(value: string, actionMeta: InputActionMeta) => {
                    if (actionMeta.action === 'input-change') {
                        setInputValue(value);
                    }
                }}
                onChange={(option: SingleValue<OptionType>) => {
                    setSelectedOption(option);
                    setInputValue('');
                    setIsMenuOpen(false);
                    if (option) {
                        const hub = hubMaps.find((h) => h.hub_index === option.value);
                        if (hub) {
                            setSelectedHubIndex(hub.hub_index.toString());
                        }
                    } else {
                        setInputValue('');
                        setIsMenuOpen(true);
                    }
                }}
                noOptionsMessage={() => '選択できるピンがありません'}
            />
            <IconAdaptive Icon={Search} baseSize={20} breakpoints={{ md: 20 }} />
        </form>
    );
}
